import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Alert,
    Box,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import { Bar, Doughnut, Line, Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import { IStore } from '../../store/types';
import { IMockupGenerationLog } from '../../api/mockupGenerationLogs';
import { aggregateGenerationLogs, ICount, LOST_SUBJECT_THRESHOLD } from '../../lib/mockupGenerationLogs/aggregate';
import { getChartPalette, withAlpha } from '../../lib/mockupGenerationLogs/palette';
import { fmtBytes, fmtNum, fmtPct, fmtRatio, fmtSec } from '../../lib/mockupGenerationLogs/format';
import { ANALYTICS_TIPS } from '../../lib/mockupGenerationLogs/tips';
import MockupGenerationLogsInfoTip, { ITip } from '../MockupGenerationLogsInfoTip';

ChartJS.register(ArcElement, BarElement, CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Title, Tooltip);

interface Props {
    rows: IMockupGenerationLog[];
    totalCount: number;
    truncated: boolean;
}

function Card({ title, subtitle, tip, children, height = 260 }: { title: string; subtitle?: string; tip?: ITip; children: React.ReactNode; height?: number | 'auto' }) {
    const theme = useTheme();
    return (
        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                {title}
                {tip ? <MockupGenerationLogsInfoTip tip={tip} /> : null}
            </Typography>
            {subtitle ? (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mb: 1 }}>{subtitle}</Typography>
            ) : <Box sx={{ mb: 1 }} />}
            <Box sx={{ position: 'relative', height, flexGrow: 1 }}>{children}</Box>
        </Paper>
    );
}

function Kpi({ label, value, hint, color }: { label: string; value: string; hint?: string; color?: string }) {
    const theme = useTheme();
    return (
        <Paper sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>{label}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: color || theme.palette.text.primary, lineHeight: 1.2 }}>{value}</Typography>
            {hint ? <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{hint}</Typography> : null}
        </Paper>
    );
}

export default function MockupGenerationLogsAnalytics({ rows, totalCount, truncated }: Props) {
    const theme = useTheme();
    const mode = useSelector<IStore, 'light' | 'dark'>((s) => s.theme?.mode || 'light');
    const pal = useMemo(() => getChartPalette(mode), [mode]);
    const agg = useMemo(() => aggregateGenerationLogs(rows), [rows]);
    const { kpis } = agg;

    const baseOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { labels: { color: pal.text, boxWidth: 12 } },
                tooltip: { mode: 'index' as const, intersect: false },
                // chartjs-plugin-datalabels is registered globally by other screens
                datalabels: { display: false },
            },
            scales: {
                x: { ticks: { color: pal.text, maxRotation: 0, autoSkip: true }, grid: { color: pal.grid } },
                y: { ticks: { color: pal.text }, grid: { color: pal.grid }, beginAtZero: true },
            },
        }),
        [pal]
    );

    const doughnutOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            cutout: '60%',
            plugins: { legend: { position: 'right' as const, labels: { color: pal.text, boxWidth: 12 } }, datalabels: { display: false } },
        }),
        [pal]
    );

    const countsToDoughnut = (counts: ICount[], colors: string[]) => ({
        labels: counts.map((c) => `${c.label} (${c.count})`),
        datasets: [
            {
                data: counts.map((c) => c.count),
                backgroundColor: counts.map((_, i) => colors[i % colors.length]),
                borderColor: theme.palette.background.paper,
                borderWidth: 2,
            },
        ],
    });

    const countsToBar = (counts: ICount[], color: string) => ({
        labels: counts.map((c) => c.label),
        datasets: [{ label: 'Requests', data: counts.map((c) => c.count), backgroundColor: color, borderRadius: 4, maxBarThickness: 28 }],
    });

    const horizontalBarOptions = {
        ...baseOptions,
        indexAxis: 'y' as const,
        plugins: {
            ...baseOptions.plugins,
            legend: { display: false },
            datalabels: { display: true, anchor: 'end' as const, align: 'end' as const, color: pal.text, font: { size: 11 }, formatter: (v: number) => (v ? v : '') },
        },
        layout: { padding: { right: 32 } },
        scales: {
            x: { ticks: { color: pal.text }, grid: { color: pal.grid }, beginAtZero: true },
            y: { ticks: { color: pal.text }, grid: { display: false } },
        },
    };

    const histogramOptions = {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            legend: { display: false },
            datalabels: { display: true, anchor: 'end' as const, align: 'end' as const, color: pal.text, font: { size: 11 }, formatter: (v: number) => (v ? v : '') },
        },
        layout: { padding: { top: 16 } },
    };

    const statusColors = agg.byStatus.map((c) => (c.label === 'ok' ? pal.status.good : pal.status.critical));
    const errorKindColor = (label: string) =>
        ({
            silhouette: pal.status.warning,
            background_removal: pal.status.serious,
            validation: pal.muted,
            unavailable: pal.series[6],
            internal: pal.status.critical,
        } as Record<string, string>)[label] || pal.series[7];

    const timelineData = {
        labels: agg.timeline.map((b) => b.label),
        datasets: [
            { label: 'OK', data: agg.timeline.map((b) => b.ok), backgroundColor: pal.status.good, stack: 's', borderRadius: 2, maxBarThickness: 24 },
            { label: 'Error', data: agg.timeline.map((b) => b.error), backgroundColor: pal.status.critical, stack: 's', borderRadius: 2, maxBarThickness: 24 },
        ],
    };

    const latencyData = {
        labels: agg.timeline.map((b) => b.label),
        datasets: [
            { label: 'p50 totalSec', data: agg.timeline.map((b) => b.p50Sec), borderColor: pal.series[0], backgroundColor: withAlpha(pal.series[0], 0.15), borderWidth: 2, pointRadius: 3, tension: 0.25, spanGaps: true },
            { label: 'p95 totalSec', data: agg.timeline.map((b) => b.p95Sec), borderColor: pal.series[1], backgroundColor: withAlpha(pal.series[1], 0.15), borderWidth: 2, pointRadius: 3, tension: 0.25, spanGaps: true, borderDash: [6, 4] },
            { label: 'avg totalSec', data: agg.timeline.map((b) => b.avgSec), borderColor: pal.series[2], backgroundColor: withAlpha(pal.series[2], 0.15), borderWidth: 2, pointRadius: 3, tension: 0.25, spanGaps: true },
        ],
    };

    const stagesData = {
        labels: ['Avg pipeline (ok requests)'],
        datasets: agg.stages.map((s, i) => ({
            label: `${s.label} (${s.avgSec.toFixed(3)} s)`,
            data: [Number(s.avgSec.toFixed(4))],
            backgroundColor: pal.series[i % pal.series.length],
            stack: 'pipeline',
            borderColor: theme.palette.background.paper,
            borderWidth: 1,
        })),
    };
    const stagesOptions = {
        ...horizontalBarOptions,
        plugins: { ...baseOptions.plugins, legend: { position: 'bottom' as const, labels: { color: pal.text, boxWidth: 12 } } },
        layout: { padding: { right: 0 } },
        scales: {
            x: { stacked: true, ticks: { color: pal.text, callback: (v: any) => `${v} s` }, grid: { color: pal.grid }, beginAtZero: true },
            y: { stacked: true, ticks: { color: pal.text }, grid: { display: false } },
        },
    };

    const flagsData = {
        labels: agg.flags.map((f) => f.label),
        datasets: [
            {
                label: 'Share true',
                data: agg.flags.map((f) => Number((f.share * 100).toFixed(1))),
                backgroundColor: pal.series[0],
                borderRadius: 4,
                maxBarThickness: 18,
            },
        ],
    };
    const flagsOptions = {
        ...horizontalBarOptions,
        plugins: {
            ...horizontalBarOptions.plugins,
            datalabels: { display: true, anchor: 'end' as const, align: 'end' as const, color: pal.text, font: { size: 11 }, formatter: (v: number) => (v ? `${v}%` : '') },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => {
                        const f = agg.flags[ctx.dataIndex];
                        return `${f.trueCount} / ${f.known} known (${ctx.parsed.x}%)`;
                    },
                },
            },
        },
        scales: {
            x: { ticks: { color: pal.text, callback: (v: any) => `${v}%` }, grid: { color: pal.grid }, min: 0, max: 110 },
            y: { ticks: { color: pal.text }, grid: { display: false } },
        },
    };

    const scatterOptions = (xLabel: string, yLabel: string) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { labels: { color: pal.text, boxWidth: 12 } },
            datalabels: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx: any) => `${xLabel}: ${ctx.parsed.x} · ${yLabel}: ${ctx.parsed.y}`,
                },
            },
        },
        scales: {
            x: { title: { display: true, text: xLabel, color: pal.text }, ticks: { color: pal.text }, grid: { color: pal.grid }, beginAtZero: true },
            y: { title: { display: true, text: yLabel, color: pal.text }, ticks: { color: pal.text }, grid: { color: pal.grid }, beginAtZero: true },
        },
    });

    const mpScatter = {
        datasets: [
            { label: 'ok', data: agg.scatterMpVsSec.filter((p) => p.ok), backgroundColor: withAlpha(pal.status.good, 0.55), pointRadius: 4 },
            { label: 'error', data: agg.scatterMpVsSec.filter((p) => !p.ok), backgroundColor: withAlpha(pal.status.critical, 0.7), pointRadius: 5 },
        ],
    };
    const qualityScatter = {
        datasets: [
            { label: 'ok requests', data: agg.scatterQualityVsRetention, backgroundColor: withAlpha(pal.series[0], 0.55), pointRadius: 4 },
        ],
    };

    if (!rows.length) {
        return (
            <Box sx={{ textAlign: 'center', p: 6, color: theme.palette.text.secondary }}>
                No data for the selected filters
            </Box>
        );
    }

    return (
        <Box>
            {truncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Analytics are computed on the newest {rows.length.toLocaleString()} of {totalCount.toLocaleString()} matching records.
                    Narrow the date range for exact numbers.
                </Alert>
            )}

            {/* ---- KPIs ---- */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', mb: 1 }}>
                Key indicators
                <MockupGenerationLogsInfoTip tip={ANALYTICS_TIPS.kpis} />
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Requests" value={fmtNum(kpis.total)} hint={`${kpis.ok} ok · ${kpis.error} error`} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Error rate" value={fmtPct(kpis.errorRate)} color={kpis.errorRate > 0.05 ? pal.status.critical : kpis.errorRate > 0.01 ? pal.status.warning : pal.status.good} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Total time p50 / p95" value={`${fmtSec(kpis.p50TotalSec)} / ${fmtSec(kpis.p95TotalSec)}`} hint={`avg ${fmtSec(kpis.avgTotalSec)} · max ${fmtSec(kpis.maxTotalSec)}`} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="RemoveBG avg" value={fmtSec(kpis.avgRbSec)} hint={`3D avg ${fmtSec(kpis.avgP3dSec)}`} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="RB quality avg" value={fmtRatio(kpis.avgQuality, 3)} hint={`conf ${fmtRatio(kpis.avgConfidence, 2)} · edge ${fmtRatio(kpis.avgEdgeQuality, 2)}`} color={(kpis.avgQuality ?? 0) >= 0.7 ? pal.status.good : pal.status.warning} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="GLB avg" value={fmtBytes(kpis.avgGlbBytes)} hint={`${fmtNum(kpis.avgTriangles)} triangles avg`} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Lost subject" value={fmtNum(kpis.lostSubjectCount)} hint={`silIgnoredShare > ${LOST_SUBJECT_THRESHOLD}`} color={kpis.lostSubjectCount ? pal.status.warning : undefined} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Multi-component silhouettes" value={fmtNum(kpis.multiComponentCount)} hint={`${fmtPct(kpis.silComponentsKnown ? kpis.multiComponentCount / kpis.silComponentsKnown : 0)} of ${kpis.silComponentsKnown}`} /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="RB retries" value={fmtNum(kpis.retryCount)} hint="1024 → 512 retry" /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Fast path (noBg)" value={fmtNum(kpis.noBgFastPathCount)} hint="client sent no-bg image" /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Texture from source" value={fmtPct(kpis.texFromSourceShare)} hint="sharp texture available" /></Grid>
                <Grid item xs={6} sm={4} md={2}><Kpi label="Overrides used" value={fmtNum(kpis.overridesUsedCount)} hint={`avg input ${fmtRatio(kpis.avgSourceMp, 2)} MP`} /></Grid>
            </Grid>

            {/* ---- Timeline ---- */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={7}>
                    <Card title={`Requests per ${agg.timelineUnit} (UTC)`} tip={ANALYTICS_TIPS.timeline} subtitle="OK vs error, stacked">
                        <Bar data={timelineData} options={{ ...baseOptions, scales: { x: { ...baseOptions.scales.x, stacked: true }, y: { ...baseOptions.scales.y, stacked: true } } }} />
                    </Card>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Card title={`Latency per ${agg.timelineUnit}`} tip={ANALYTICS_TIPS.latency} subtitle="totalSec percentiles">
                        <Line data={latencyData} options={{ ...baseOptions, scales: { ...baseOptions.scales, y: { ...baseOptions.scales.y, ticks: { color: pal.text, callback: (v: any) => `${v} s` } } } }} />
                    </Card>
                </Grid>
            </Grid>

            {/* ---- Result ---- */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card title="Status" tip={ANALYTICS_TIPS.status} height={200}>
                        <Doughnut data={countsToDoughnut(agg.byStatus, statusColors)} options={doughnutOptions} />
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card title="Error kind" tip={ANALYTICS_TIPS.errorKind} subtitle={agg.byErrorKind.length ? undefined : 'no errors in range'} height={200}>
                        {agg.byErrorKind.length ? (
                            <Doughnut data={countsToDoughnut(agg.byErrorKind, agg.byErrorKind.map((c) => errorKindColor(c.label)))} options={doughnutOptions} />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: pal.status.good, fontWeight: 700 }}>0 errors</Box>
                        )}
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card title="HTTP status" tip={ANALYTICS_TIPS.httpStatus} height={200}>
                        <Bar data={countsToBar(agg.byHttpStatus, pal.series[0])} options={horizontalBarOptions} />
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card title="Total time distribution" tip={ANALYTICS_TIPS.totalSecHistogram} subtitle="totalSec buckets" height={200}>
                        <Bar data={countsToBar(agg.totalSecHistogram, pal.series[0])} options={histogramOptions} />
                    </Card>
                </Grid>
            </Grid>

            {/* ---- Pipeline ---- */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={7}>
                    <Card title="Average pipeline breakdown" tip={ANALYTICS_TIPS.stages} subtitle="seconds per stage, ok requests only; 'other' = totalSec − known stages" height={220}>
                        <Bar data={stagesData} options={stagesOptions} />
                    </Card>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Card title="Input size vs total time" tip={ANALYTICS_TIPS.mpScatter} subtitle="sourceMp → totalSec (each point is a request)" height={220}>
                        <Scatter data={mpScatter} options={scatterOptions('source, MP', 'totalSec')} />
                    </Card>
                </Grid>
            </Grid>

            {/* ---- Quality ---- */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                    <Card title="RB overall quality" tip={ANALYTICS_TIPS.qualityHistogram} subtitle="rbOverallQuality buckets (ok requests)" height={220}>
                        <Bar data={countsToBar(agg.qualityHistogram, pal.series[2])} options={histogramOptions} />
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card title="Quality vs content retention" tip={ANALYTICS_TIPS.qualityScatter} subtitle="rbContentRetention → rbOverallQuality" height={220}>
                        <Scatter data={qualityScatter} options={scatterOptions('content retention', 'overall quality')} />
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card title="Silhouette components" tip={ANALYTICS_TIPS.silComponents} subtitle="p3dSilComponents; >1 means several regions on the photo" height={220}>
                        <Bar data={countsToBar(agg.silComponentsHistogram, pal.series[3])} options={histogramOptions} />
                    </Card>
                </Grid>
            </Grid>

            {/* ---- Modes & infra ---- */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={5}>
                    <Card title="Request mode flags" tip={ANALYTICS_TIPS.flags} subtitle="share of requests with the flag = true" height={400}>
                        <Bar data={flagsData} options={flagsOptions} />
                    </Card>
                </Grid>
                <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                        {[
                            { title: 'Model type', data: agg.byModelType, color: pal.series[0] },
                            { title: 'RB device', data: agg.byDevice, color: pal.series[1] },
                            { title: 'Response mode', data: agg.byResponseMode, color: pal.series[2] },
                            { title: 'Target resolution', data: agg.byTargetResolution, color: pal.series[3] },
                            { title: 'Server host', data: agg.byServerHost, color: pal.series[4] },
                            { title: 'Server version', data: agg.byServerVersion, color: pal.series[6] },
                        ].map((c) => (
                            <Grid item xs={12} sm={6} key={c.title}>
                                <Card title={c.title} tip={ANALYTICS_TIPS.distributions} height={110}>
                                    <Bar data={countsToBar(c.data.slice(0, 6), c.color)} options={horizontalBarOptions} />
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            {/* ---- Errors table ---- */}
            {agg.errorMessages.length > 0 && (
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center' }}>
                        Top error messages
                        <MockupGenerationLogsInfoTip tip={ANALYTICS_TIPS.errorMessages} />
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Kind</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell align="right">Count</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {agg.errorMessages.slice(0, 15).map((e) => (
                                <TableRow key={`${e.kind}|${e.message}`}>
                                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: errorKindColor(e.kind) }}>{e.kind}</TableCell>
                                    <TableCell>{e.message}</TableCell>
                                    <TableCell align="right">{e.count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
